/* eslint-disable react/prop-types */
import { FC, useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Mention from "@tiptap/extension-mention";
import type {
	SuggestionProps,
	SuggestionKeyDownProps
} from "@tiptap/suggestion";
import {
	Bold,
	Italic,
	Underline as UnderlineIcon,
	Link as LinkIcon,
	List,
	ListOrdered
} from "lucide-react";
import MentionList, { MentionListRef } from "./MentionList";
import {
	fetchUserMentions,
	fetchHashtagMentions,
	MentionItem
} from "./mentionSuggestions";
import { extractMentions } from "./extractMentions";

interface Props {
	content: string;
	onChange: (html: string) => void;
	placeholder?: string;
	/** Forwarded to the editor's textbox so FormControl/FormLabel/FormMessage can wire up correctly. */
	id?: string;
	"aria-describedby"?: string;
	"aria-invalid"?: boolean | "true" | "false";
	/** Called on every content update with the ids of mentioned users and hashtags. */
	onMentionsChange?: (m: { userIds: string[]; hashtagIds: string[] }) => void;
}

export const buildMention = (
	char: "@" | "#",
	mentionType: "user" | "hashtag",
	fetcher: (q: string) => Promise<MentionItem[]>
) =>
	Mention.extend({
		name: mentionType === "user" ? "userMention" : "hashtagMention",
		parseHTML() {
			return [
				{
					tag: `span[data-type="mention"][data-mention-type="${mentionType}"]`
				}
			];
		}
	}).configure({
		HTMLAttributes: {
			"data-type": "mention",
			"data-mention-type": mentionType
		},
		renderHTML({ node }) {
			return [
				"span",
				{
					"data-type": "mention",
					"data-mention-type": mentionType,
					"data-id": node.attrs.id,
					"data-label": node.attrs.label,
					class: "mention text-primary font-medium"
				},
				`${char}${node.attrs.label ?? node.attrs.id}`
			];
		},
		suggestion: {
			char,
			items: ({ query }: { query: string }) => fetcher(query),
			render: () => {
				let component: ReactRenderer<MentionListRef> | null = null;
				let popup: HTMLDivElement | null = null;
				const position = (rect: DOMRect | null) => {
					if (!popup || !rect) return;
					popup.style.left = `${rect.left + window.scrollX}px`;
					popup.style.top = `${rect.bottom + window.scrollY}px`;
				};
				return {
					onStart: (props: SuggestionProps<MentionItem>) => {
						component = new ReactRenderer(MentionList, {
							props: { items: props.items, command: props.command },
							editor: props.editor
						});
						popup = document.createElement("div");
						popup.style.position = "absolute";
						popup.style.zIndex = "50";
						popup.appendChild(component.element);
						document.body.appendChild(popup);
						position(props.clientRect?.() as DOMRect | null);
					},
					onUpdate: (props: SuggestionProps<MentionItem>) => {
						component?.updateProps({
							items: props.items,
							command: props.command
						});
						position(props.clientRect?.() as DOMRect | null);
					},
					onKeyDown: (props: SuggestionKeyDownProps) => {
						if (props.event.key === "Escape") return true;
						return component?.ref?.onKeyDown(props) ?? false;
					},
					onExit: () => {
						popup?.remove();
						component?.destroy();
						popup = null;
						component = null;
					}
				};
			}
		}
	});

const ToolbarButton: FC<{
	onClick: () => void;
	isActive?: boolean;
	title: string;
	children: React.ReactNode;
}> = ({ onClick, isActive = false, title, children }) => (
	<button
		type="button"
		onClick={onClick}
		title={title}
		aria-label={title}
		aria-pressed={isActive}
		className={[
			"p-2 rounded-md transition-colors",
			isActive
				? "bg-primary/10 text-primary"
				: "text-muted-foreground hover:bg-muted hover:text-foreground"
		].join(" ")}
	>
		{children}
	</button>
);

const RichTextEditor: FC<Props> = ({
	content,
	onChange,
	placeholder = "Write your announcement...",
	id,
	"aria-describedby": ariaDescribedby,
	"aria-invalid": ariaInvalid,
	onMentionsChange
}) => {
	const isInitialMount = useRef(true);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
			Link.configure({
				openOnClick: false,
				HTMLAttributes: {
					class: "text-primary underline",
					rel: "noopener noreferrer"
				},
				validate: (href) => /^https?:\/\//i.test(href)
			}),
			Placeholder.configure({ placeholder }),
			Underline,
			buildMention("@", "user", fetchUserMentions),
			buildMention("#", "hashtag", fetchHashtagMentions)
		],
		content,
		onUpdate: ({ editor: ed }) => {
			const html = ed.getHTML();
			onChange(html);
			onMentionsChange?.(extractMentions(html));
		},
		editorProps: {
			attributes: {
				class:
					"prose prose-sm max-w-none focus:outline-none min-h-[120px] px-4 py-3 cursor-text",
				"aria-label": placeholder,
				"aria-multiline": "true",
				role: "textbox",
				...(id ? { id } : {}),
				...(ariaDescribedby ? { "aria-describedby": ariaDescribedby } : {}),
				...(ariaInvalid !== undefined
					? { "aria-invalid": String(ariaInvalid) }
					: {})
			}
		}
	});

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		if (content !== editor.getHTML()) {
			// emitUpdate: false suppresses TipTap's onUpdate so prop-driven
			// content sync doesn't re-fire onChange (the v3 equivalent of the
			// old `setContent(content, false)` second-arg form).
			editor.commands.setContent(content, { emitUpdate: false });
		}
	}, [content, editor]);

	const setLink = useCallback(() => {
		if (!editor) return;
		const previousUrl = editor.getAttributes("link").href;
		const url = window.prompt("Enter URL:", previousUrl);
		if (url === null) return;
		if (url === "") {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			return;
		}
		const trimmed = url.trim();
		if (!/^https?:\/\//i.test(trimmed)) {
			window.alert("Only http:// and https:// URLs are allowed.");
			return;
		}
		editor
			.chain()
			.focus()
			.extendMarkRange("link")
			.setLink({ href: trimmed })
			.run();
	}, [editor]);

	if (!editor) return null;

	return (
		<div className="rounded-md border border-input bg-white overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
			<div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-subtle">
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleBold().run()}
					isActive={editor.isActive("bold")}
					title="Bold"
				>
					<Bold className="h-4 w-4" />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleItalic().run()}
					isActive={editor.isActive("italic")}
					title="Italic"
				>
					<Italic className="h-4 w-4" />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					isActive={editor.isActive("underline")}
					title="Underline"
				>
					<UnderlineIcon className="h-4 w-4" />
				</ToolbarButton>
				<div className="w-px h-5 bg-border mx-1" />
				<ToolbarButton
					onClick={setLink}
					isActive={editor.isActive("link")}
					title="Link"
				>
					<LinkIcon className="h-4 w-4" />
				</ToolbarButton>
				<div className="w-px h-5 bg-border mx-1" />
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					isActive={editor.isActive("bulletList")}
					title="Bullet list"
				>
					<List className="h-4 w-4" />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					isActive={editor.isActive("orderedList")}
					title="Numbered list"
				>
					<ListOrdered className="h-4 w-4" />
				</ToolbarButton>
			</div>
			<EditorContent editor={editor} />
		</div>
	);
};

export default RichTextEditor;
